import { Box, Badge, Text, HStack, Popover, VStack, Button, useDisclosure, CloseButton } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ItineraryAssignmentDto, AssignmentStatus, AssignmentScheduleInfoDto } from '../../../types/campaign'
import { getTurfColor } from '../../../lib/mapConfig'
import { FollowUpKindBadge } from '../followUp/FollowUpKindBadge'
import { assignmentProgressLabel, turfContactRate } from './assignmentProgress'

const STATUS_COLORS: Record<AssignmentStatus, string> = {
  Scheduled: 'gray',
  InProgress: 'blue',
  Complete: 'green',
}

interface ItineraryAssignmentCardProps {
  assignment: ItineraryAssignmentDto
  scheduleInfo?: AssignmentScheduleInfoDto
  onRemove?: (assignmentId: string) => void
}

export function ItineraryAssignmentCard({
  assignment,
  scheduleInfo,
  onRemove,
}: ItineraryAssignmentCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.id, data: { type: 'assignment', assignment } })

  const { open, onOpen, onClose } = useDisclosure()

  const turfColor = getTurfColor(assignment.turfIndex)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const contactRate = turfContactRate(assignment)

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      bg="surface"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="4px solid"
      borderLeftColor={turfColor}
      borderRadius="md"
      p={2}
      mb={1}
      cursor="grab"
      _hover={{ borderColor: 'accent', shadow: 'sm' }}
      fontSize="xs"
      userSelect="none"
    >
      <Popover.Root
        open={open}
        lazyMount
        unmountOnExit
        onOpenChange={e => {
          if (e.open) {
            onOpen();
          } else {
            onClose();
          }
        }}
        positioning={{
          placement: 'right'
        }}>
        <Popover.Trigger asChild>
          <Box cursor="pointer">
            <HStack gap={1}>
              <Box w="8px" h="8px" borderRadius="sm" bg={turfColor} flexShrink={0} mt="2px" />
              <Text fontWeight="semibold" lineHeight="short" lineClamp={1}>
                {assignment.turfLabel}
              </Text>
            </HStack>
            <Text color="gray.500" lineHeight="short" lineClamp={1}>
              {assignment.turfMapLabel}
            </Text>
            <HStack gap={1} mt={1} flexWrap="wrap">
              <Badge colorPalette={STATUS_COLORS[assignment.status]} size="sm" fontSize="2xs">
                {assignment.status}
              </Badge>
              {assignment.purpose === 'FollowUp' && assignment.followUpKind && (
                <FollowUpKindBadge kind={assignment.followUpKind} customName={assignment.followUpCustomKindName} />
              )}
              {scheduleInfo && scheduleInfo.atWorkPercent > 40 && (
                <Badge
                  colorPalette={scheduleInfo.atWorkPercent > 70 ? 'red' : 'yellow'}
                  size="sm"
                  fontSize="2xs"
                >
                  {scheduleInfo.atWorkPercent}% at work
                </Badge>
              )}
            </HStack>
            {assignment.status === 'Complete' && (
              <Text color="gray.600" mt={1} lineHeight="short">
                {assignmentProgressLabel(assignment)}
              </Text>
            )}
          </Box>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content w="280px">
            <Popover.CloseTrigger asChild><CloseButton size="sm" /></Popover.CloseTrigger>
            <Popover.Title fontSize="sm" fontWeight="semibold">
              <HStack gap={2}>
                <Box w="10px" h="10px" borderRadius="sm" bg={turfColor} />
                <Text>{assignment.turfLabel}</Text>
              </HStack>
            </Popover.Title>
            <Popover.Body>
              <VStack gap={3} align="stretch">
                <Box>
                  <Text fontSize="2xs" color="gray.500">Status</Text>
                  <Badge colorPalette={STATUS_COLORS[assignment.status]} fontSize="xs">
                    {assignment.status}
                  </Badge>
                </Box>
                <HStack gap={4}>
                  <Box>
                    <Text fontSize="2xs" color="gray.500">Doors Knocked</Text>
                    <Text fontSize="sm" fontWeight="medium">{assignment.doorsKnocked}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="2xs" color="gray.500">Contacts Made</Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {assignment.contactsMade}
                    </Text>
                  </Box>
                </HStack>
                {assignment.turfMemberCount > 0 && (
                  <Box>
                    <Text fontSize="2xs" color="gray.500">Turf Contacted (all days)</Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {assignment.turfVisitedCount} / {assignment.turfMemberCount} ({contactRate}%)
                    </Text>
                  </Box>
                )}
                {scheduleInfo && (
                  <Box>
                    <Text fontSize="2xs" color="gray.500">Schedule Estimate</Text>
                    <HStack gap={3} fontSize="sm">
                      <Text color="green.600" fontWeight="medium">{scheduleInfo.atHomeCount} home</Text>
                      <Text color="orange.500" fontWeight="medium">{scheduleInfo.atWorkCount} work</Text>
                      {scheduleInfo.unknownCount > 0 && (
                        <Text color="gray.400">{scheduleInfo.unknownCount} unknown</Text>
                      )}
                    </HStack>
                  </Box>
                )}
                {onRemove && (
                  <Button size="sm" colorPalette="red" variant="outline" onClick={() => onRemove(assignment.id)}>
                    Remove
                  </Button>
                )}
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Box>
  );
}
